// Renders a message: BlockMarkdown decides the KIND of each block, MessageAttributedText decides
// which characters are bold / a link / a mention, this file only draws. Never `innerHTML`: every
// character the server sent ends up as a text node, so a message cannot inject markup.
import { Fragment, memo, useContext, useState, type JSX, type ReactNode } from 'react';
import { BlockMarkdown, type Block } from '../../core/blockMarkdown';
import { MessageAttributedText, type MessageTextRun } from '../../core/messageAttributedText';
import { Icon } from '../../design/icons';
import { t } from '../../i18n/i18n';
import { mediaUrls } from '../media';
import { permalinkPostId } from '../router';
import { ShellContext } from '../shell/ShellContext';
import { ChannelLink, useKnownChannelSlugs } from '../features/compose/MessageLinks';

export interface BodyContext {
  knownUsernames: ReadonlySet<string>;
  currentUsername: string | null;
  customEmoji: Readonly<Record<string, { id: string; updateAt: number }>>;
  onMention?(username: string, anchor?: { x: number; y: number }): void;
}

const CUSTOM_EMOJI = /:([a-zA-Z0-9_+-]{1,64}):/g;

function withCustomEmoji(text: string, ctx: BodyContext, keyBase: string): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  let i = 0;
  for (const m of text.matchAll(CUSTOM_EMOJI)) {
    const emoji = ctx.customEmoji[m[1]];
    if (!emoji) continue;
    if (m.index! > last) out.push(text.slice(last, m.index));
    // A custom emoji not loaded (or unloadable) keeps its `:name:` text — never a hole.
    out.push(<img key={`${keyBase}-e${i++}`} className="custom-emoji" src={mediaUrls.customEmoji(emoji.id)} alt={m[0]} title={m[0]} loading="lazy" />);
    last = m.index! + m[0].length;
  }
  if (last === 0) return [text];
  if (last < text.length) out.push(text.slice(last));
  return out;
}

function renderRun(run: MessageTextRun, ctx: BodyContext, key: string): ReactNode {
  if (run.image) {
    return <img key={key} className="custom-emoji" style={{ borderRadius: 6 }} src={run.image.url} alt={run.image.alt ?? ''} loading="lazy" referrerPolicy="no-referrer" />;
  }
  let node: ReactNode = withCustomEmoji(run.text, ctx, key);
  if (run.code) node = <code>{node}</code>;
  if (run.strikethrough) node = <s>{node}</s>;
  if (run.italic) node = <em>{node}</em>;
  if (run.bold && !run.mention) node = <strong>{node}</strong>;
  if (run.mention) {
    const username = run.mention.username;
    return (
      <span
        key={key}
        className={`mention${run.mention.isSelf ? ' self' : ''}`}
        role="link"
        tabIndex={0}
        onClick={(e) => {
          const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
          ctx.onMention?.(username, { x: r.left, y: r.bottom + 6 });
        }}
        onKeyDown={(e) => e.key === 'Enter' && ctx.onMention?.(username)}
      >
        {node}
      </span>
    );
  }
  if (run.channelSlug) return <ChannelLink key={key} slug={run.channelSlug}>{node}</ChannelLink>;
  if (run.link && /^(https?:|mailto:)/i.test(run.link)) {
    return (
      <MessageLink key={key} href={run.link}>
        {node}
      </MessageLink>
    );
  }
  return <Fragment key={key}>{node}</Fragment>;
}

export function Inline({ text, ctx }: { text: string; ctx: BodyContext }) {
  const knownChannelSlugs = useKnownChannelSlugs(); // ~channel links (features/compose)
  const rendered = MessageAttributedText.attributed(text, {
    knownChannelSlugs,
    knownUsernames: ctx.knownUsernames,
    currentUsername: ctx.currentUsername,
  });
  return <>{rendered.runs.map((run, i) => renderRun(run, ctx, String(i)))}</>;
}

function renderBlock(block: Block, ctx: BodyContext, key: number): JSX.Element {
  switch (block.kind) {
    case 'paragraph':
      return (
        <p key={key}>
          {block.text.split('\n').map((line, i, all) => (
            <Fragment key={i}>
              <Inline text={line} ctx={ctx} />
              {i < all.length - 1 ? <br /> : null}
            </Fragment>
          ))}
        </p>
      );
    case 'heading': {
      const H = (`h${Math.min(3, Math.max(1, block.level))}`) as 'h1' | 'h2' | 'h3';
      return (
        <H key={key}>
          <Inline text={block.text} ctx={ctx} />
        </H>
      );
    }
    case 'quote':
      return (
        <blockquote key={key}>
          <Inline text={block.text} ctx={ctx} />
        </blockquote>
      );
    case 'bullets':
      return (
        <ul key={key}>
          {block.items.map((item, i) => (
            <li key={i}>
              <Inline text={item} ctx={ctx} />
            </li>
          ))}
        </ul>
      );
    case 'numbered':
      return (
        <ol key={key}>
          {block.items.map((item, i) => (
            <li key={i}>
              <Inline text={item} ctx={ctx} />
            </li>
          ))}
        </ol>
      );
    case 'codeFence':
      return (
        <pre key={key}>
          <code>{block.code}</code>
        </pre>
      );
    case 'rule':
      return <hr key={key} />;
    case 'table': {
      // CARD design (the user rejected the grid look): hairline ROW separators only; a header
      // whose cells are all blank is hidden.
      const hasHeader = block.header.some((h) => h.trim() !== '');
      return (
        <div key={key} className="table-card">
          <table>
            {hasHeader ? (
              <thead>
                <tr>
                  {block.header.map((h, i) => (
                    <th key={i}>
                      <Inline text={h} ctx={ctx} />
                    </th>
                  ))}
                </tr>
              </thead>
            ) : null}
            <tbody>
              {block.rows.map((row, r) => (
                <tr key={r}>
                  {row.map((cell, c) => (
                    <td key={c}>
                      <Inline text={cell} ctx={ctx} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
  }
}

/** Long messages fold behind "Ver más" (the Mac's 1.0.281 behaviour), measured by content. */
function isLong(message: string): boolean {
  if (message.length > 900) return true;
  let lines = 1;
  for (let i = 0; i < message.length; i++) if (message.charCodeAt(i) === 10 && ++lines > 14) return true;
  return false;
}

export const MessageBody = memo(function MessageBody({ message, ctx }: { message: string; ctx: BodyContext }) {
  const [expanded, setExpanded] = useState(false);
  const blocks = BlockMarkdown.parse(message);
  const long = isLong(message);
  const content = <div className="md">{blocks.map((b, i) => renderBlock(b, ctx, i))}</div>;
  if (!long) return content;
  return (
    <>
      <div className={expanded ? undefined : 'fold'}>{content}</div>
      <button className="see-more" onClick={() => setExpanded(!expanded)}>
        {expanded ? t('channel.seeLess') : t('channel.seeMore')}
        <Icon name={expanded ? 'chevronUp' : 'chevronDown'} size={12} strokeWidth={2} />
      </button>
    </>
  );
});

/** A link in a message. One of OUR message permalinks jumps in place (it used to open a new tab of
 *  the old webapp); anything else opens in a new tab. A modified click keeps the browser's own say. */
function MessageLink({ href, children }: { href: string; children: ReactNode }) {
  const shell = useContext(ShellContext);
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      onClick={(e) => {
        if (!shell || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        const postId = permalinkPostId(href);
        if (!postId) return;
        e.preventDefault();
        shell.jumpToPost(postId);
      }}
    >
      {children}
    </a>
  );
}
